import type { PaymentErrorCategory } from "./PaymentErrorCategories";
import type { PaymentErrorMetadata } from "./PaymentErrorMetadata";

/**
 * Base class for all payment orchestration errors.
 * Separates technical debugging information from user-friendly messages.
 *
 * The `.message` property contains technical details for developers, logs, and monitoring (Sentry).
 * The `.userMessage` property contains user-facing text for UI toasts and notifications.
 *
 * Metadata is designed to be hydrated at the catch point (PaymentManager) rather than
 * at the throw point, keeping error sites clean and simple.
 */
export abstract class PaymentOrchestrationError extends Error {
  /**
   * Error category for handling logic
   */
  abstract readonly category: PaymentErrorCategory;

  /**
   * Whether this error can potentially be recovered from automatically
   */
  abstract readonly recoverable: boolean;

  /**
   * User-friendly message for UI display (toasts, notifications)
   */
  readonly userMessage: string;

  /**
   * Additional metadata for debugging and error handling
   * This is typically hydrated at the catch point with full state information
   */
  metadata?: PaymentErrorMetadata | Record<string, unknown>;

  constructor(
    technicalMessage: string,
    options?: {
      userMessage?: string;
      metadata?: PaymentErrorMetadata | Record<string, unknown>;
    }
  ) {
    // Store technical message in .message for logs, Sentry, and debugging
    super(technicalMessage);
    this.name = this.constructor.name;

    // Store user-friendly message separately for UI display
    this.userMessage =
      options?.userMessage ||
      "Something unexpected happened. Please try again or contact support.";

    this.metadata = options?.metadata;
  }

  /**
   * Creates a detailed error report for logging
   */
  toErrorReport(): {
    name: string;
    message: string;
    userMessage: string;
    category: string;
    recoverable: boolean;
    metadata?: PaymentErrorMetadata | Record<string, unknown>;
    stack?: string;
  } {
    return {
      name: this.name,
      message: this.message, // Technical message for logs/Sentry
      userMessage: this.userMessage, // User-friendly message for UI
      category: this.category,
      recoverable: this.recoverable,
      metadata: this.metadata,
      stack: this.stack,
    };
  }
}
